import { Star, StarHalf } from "lucide-react";
import Image from "next/image";
import React from "react";
import Credit from "./credit/Credit";
import { cn } from "@/lib/utils";
import { isFilled, RichTextField } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";

export interface TestimonialProps {
  variant: "default" | "photoWithQuote";
  quote: RichTextField;
  photographer: string;
  rating?: number;
  photographerImage: string;
  mainImage: string;
  className?: string;
}

export const clampRating = (rating: number) => Math.min(Math.max(rating, 1), 5);

export default function Testimonial({
  className,
  variant,
  quote,
  photographer,
  rating = 5,
  photographerImage,
  mainImage,
}: TestimonialProps) {
  const clampedRating = clampRating(rating);

  return (
    <>
      {variant === "photoWithQuote" && (
        <figure
          className={cn(
            "font-cabinet-grotesk relative aspect-square h-full max-h-[24rem] w-full max-w-[23.625rem] overflow-hidden rounded-xl bg-[rgba(0,0,0,0.06)]",
            className
          )}
        >
          <Image alt="Testimonial image" src={mainImage} fill className="absolute object-cover" priority />
          <figcaption className="absolute bottom-4 left-1/2 z-10 w-[90%] -translate-x-1/2 transform">
            <Credit
              variant="headingDown"
              state="open"
              name={photographer}
              image={photographerImage}
              description={quote}
              className="max-w-full"
            />
          </figcaption>
        </figure>
      )}
      {variant === "default" && (
        <figure
          className={cn(
            "bg-festi-pink-50 font-cabinet-grotesk flex aspect-square h-full max-h-[24rem] w-full max-w-[23.625rem] flex-col justify-between rounded-lg p-3 xl:p-6 pr-4",
            className
          )}
        >
          {isFilled.richText(quote) && (
            <blockquote
              cite="#"
              className="text-pink-primary line-clamp-[7] overflow-hidden text-[clamp(1.125rem,4vw,1.813rem)] leading-snug font-medium break-words hyphens-auto"
            >
              <PrismicRichText field={quote} />
            </blockquote>
          )}

          <figcaption className="flex items-end justify-between flex-wrap">
            <p className="text-pink-primary w-fit text-lg leading-normal font-medium">{photographer}</p>

            <div className="bg-pink-primary flex items-center gap-2 rounded-full border-none px-3 py-1.5">
              <span className="w-fit text-sm leading-normal font-medium text-white sm:text-[15px]">
                {clampedRating}/5
              </span>

              <div
                role="group"
                aria-label={`Rating: ${clampedRating} out of 5`}
                className="flex items-center gap-[3px]"
              >
                {Array.from({ length: Math.floor(clampedRating) }, (_, index) => (
                  <Star aria-hidden="true" key={index} className="fill-white text-white" size={18} />
                ))}
                {clampedRating % 1 !== 0 && <StarHalf aria-hidden="true" className="fill-white text-white" size={18} />}
              </div>
            </div>
          </figcaption>
        </figure>
      )}
    </>
  );
}
